import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { GlassCard } from '@/components/brand/GlassCard';
import { adminListConsultations, adminUpdateConsultation } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

export default function AdminConsultationsPage() {
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await adminListConsultations(status ? { status } : {});
            setItems(data);
        } catch {
            toast.error('Could not load consultations.');
        } finally {
            setLoading(false);
        }
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const setStatusOf = async (cid, newStatus, notes) => {
        await adminUpdateConsultation(cid, { status: newStatus, internal_notes: notes || '' });
        toast.success('Updated.');
        load();
    };

    return (
        <AdminLayout title="Consultations">
            <div className="mb-4 flex items-center gap-2" data-testid="admin-filter">
                <span className="meta-eyebrow">Filter</span>
                {['', 'new', 'in_progress', 'closed'].map((s) => (
                    <button key={s || 'all'} onClick={() => setStatus(s)} className={`text-[11px] tracking-[0.14em] uppercase px-2.5 py-1 rounded-la-sm border ${status === s ? 'border-la-gold/40 text-la-gold bg-la-gold/10' : 'border-white/10 text-la-text2 hover:text-la-text'}`}>
                        {s || 'All'}
                    </button>
                ))}
            </div>
            <GlassCard className="p-0 overflow-hidden">
                <div className="overflow-x-auto">
                    <table data-testid="admin-table" className="w-full text-sm">
                        <thead className="bg-white/[0.04]">
                            <tr className="text-[11px] tracking-[0.14em] uppercase text-la-text3">
                                <Th>Client</Th><Th>Category</Th><Th>Urgency</Th><Th>Mode</Th><Th>Status</Th><Th>Created</Th><Th></Th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={7} className="px-4 py-8 text-center text-la-text3">Loading…</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={7} className="px-4 py-10 text-center text-la-text3">No consultations.</td></tr>
                            ) : items.map((c) => (
                                <tr key={c.consultation_id} className="border-t border-white/10 hover:bg-white/[0.03]">
                                    <Td>
                                        <div className="text-la-text">{c.name}</div>
                                        <div className="text-[11px] text-la-text3">{c.email}</div>
                                    </Td>
                                    <Td>{c.category}</Td>
                                    <Td className="capitalize">{c.urgency}</Td>
                                    <Td className="capitalize">{(c.contact_mode || '').replace('_',' ')}</Td>
                                    <Td><span className="text-[10px] tracking-[0.16em] uppercase">{c.status}</span></Td>
                                    <Td>{formatDate(c.created_at)}</Td>
                                    <Td>
                                        <ConsultationDetail c={c} onUpdate={setStatusOf} />
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </GlassCard>
        </AdminLayout>
    );
}

const Th = ({ children }) => <th className="text-left px-4 py-3 font-medium">{children}</th>;
const Td = ({ children, className = '' }) => <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;

const ConsultationDetail = ({ c, onUpdate }) => {
    const [open, setOpen] = useState(false);
    const [notes, setNotes] = useState(c.internal_notes || '');
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button className="text-xs text-la-text2 hover:text-la-text underline-grow" data-testid="admin-view-consultation">View</button>
            </DialogTrigger>
            <DialogContent className="bg-la-panel border border-white/10 text-la-text max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">{c.name} · {c.category}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><div className="meta-eyebrow">Email</div><div>{c.email}</div></div>
                    <div><div className="meta-eyebrow">Phone</div><div>{c.phone || '—'}</div></div>
                    <div><div className="meta-eyebrow">Urgency</div><div className="capitalize">{c.urgency}</div></div>
                    <div><div className="meta-eyebrow">Mode</div><div className="capitalize">{(c.contact_mode || '').replace('_',' ')}</div></div>
                    <div><div className="meta-eyebrow">Preferred contact</div><div className="capitalize">{c.preferred_contact}</div></div>
                    <div><div className="meta-eyebrow">Created</div><div>{formatDate(c.created_at)}</div></div>
                </div>
                <div className="mt-2">
                    <div className="meta-eyebrow">Summary</div>
                    <p className="mt-2 text-sm text-la-text2 whitespace-pre-wrap">{c.summary}</p>
                </div>
                {(c.file_ids || []).length > 0 && (
                    <div className="mt-2">
                        <div className="meta-eyebrow">Documents</div>
                        <ul className="mt-2 space-y-1 text-sm">
                            {c.file_ids.map((fid) => (
                                <li key={fid}>
                                    <a className="text-la-text2 hover:text-la-text underline-grow" href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}`} target="_blank" rel="noreferrer">{fid}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="mt-2">
                    <div className="meta-eyebrow">Internal notes</div>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-2 w-full rounded-la-sm bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-la-text" />
                </div>
                <div className="mt-3 flex items-center justify-end gap-2 flex-wrap">
                    {['new','in_progress','closed'].map((s) => (
                        <button key={s} onClick={() => onUpdate(c.consultation_id, s, notes)} className="text-[11px] tracking-[0.14em] uppercase px-2.5 py-1.5 rounded-la-sm border border-white/10 hover:bg-white/[0.05] text-la-text2">
                            Mark {s.replace('_',' ')}
                        </button>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
};
